<template>
    <aside class="guide-sidebar">
        <!-- Mobile toggle: collapses the whole nav rail on narrow viewports. Desktop ignores this. -->
        <div class="guide-sidebar__mobile-toggle" @click="mobileOpen = !mobileOpen">
            <span>
                <q-icon name="menu_book" size="18px" class="q-mr-sm" />
                {{ activeTopicLabel || 'Guides' }}
            </span>
            <q-icon :name="mobileOpen ? 'expand_less' : 'expand_more'" size="20px" />
        </div>

        <nav
            class="guide-sidebar__nav"
            :class="{ 'guide-sidebar__nav--open': mobileOpen }"
            aria-label="Integration Guides navigation"
        >
            <div class="guide-sidebar__search">
                <div class="guide-sidebar__search-box">
                    <q-icon name="search" size="16px" class="guide-sidebar__search-icon" />
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Filter topics…"
                        class="guide-sidebar__search-input"
                    />
                    <q-icon
                        v-if="searchQuery"
                        name="close"
                        size="14px"
                        class="guide-sidebar__search-clear"
                        @click="searchQuery = ''"
                    />
                </div>
            </div>

            <div v-for="group in filteredGroups" :key="group.key" class="guide-sidebar__group">
                <div class="guide-sidebar__group-label">{{ group.label }}</div>
                <ul class="guide-sidebar__topics">
                    <li v-for="topic in group.topics" :key="topic.key">
                        <a
                            :href="`${basePath}?topic=${topic.key}`"
                            :class="[
                                'guide-sidebar__topic',
                                { 'guide-sidebar__topic--active': topic.key === activeTopic }
                            ]"
                            @click="onTopicClick($event, topic.key)"
                        >
                            <q-icon :name="topic.icon" size="18px" class="guide-sidebar__topic-icon" />
                            <span class="guide-sidebar__topic-label">{{ topic.text }}</span>
                        </a>
                    </li>
                </ul>
            </div>

            <div v-if="searchQuery && filteredGroups.length === 0" class="guide-sidebar__no-results">
                No matching topics
            </div>
        </nav>
    </aside>
</template>

<script>
/**
 * In-page navigation rail for a docs area (Developer Guides or Platform Guides).
 *
 * Rendered in the content area in both authenticated and public modes - the Dashboard
 * sidebar (when auth'd) just links to the docs page, it does not duplicate per-topic
 * nav. This keeps the docs experience consistent regardless of auth state and matches
 * the Stripe/Vercel docs pattern.
 *
 * Navigation strategy: clicking a topic updates the URL's ?topic= query parameter via
 * history.pushState and emits a 'navigate' event to the parent, which swaps the active
 * component without a full page reload. The underlying <a href="..."> fallback ensures
 * middle-click / cmd-click / right-click-open-in-new-tab all work correctly.
 *
 * The sidebar is topic-list agnostic: callers pass in a `groups` prop (matching the
 * shape in developerTopics.js / platformTopics.js) so a single component serves both
 * Developer Guides and Platform Guides.
 */
export default {
    name: 'GuideSidebar',

    props: {
        // Array of { key, label, topics: [{ key, text, icon, component }] }.
        groups: { type: Array, required: true },
        activeTopic: { type: String, required: true },
        // URL path for the topic links - derived from the parent page's route.
        basePath: { type: String, default: '/Docs/IntegrationGuides' }
    },

    emits: ['navigate'],

    data() {
        return {
            mobileOpen: false,
            searchQuery: ''
        };
    },

    computed: {
        activeTopicLabel() {
            for (const group of this.groups) {
                const match = group.topics.find((t) => t.key === this.activeTopic);
                if (match) return match.text;
            }
            return null;
        },

        filteredGroups() {
            var q = String(this.searchQuery || '').trim().toLowerCase();
            if (!q) return this.groups;

            var result = [];
            for (var i = 0; i < this.groups.length; i++) {
                var group = this.groups[i];
                var topics = [];
                for (var j = 0; j < group.topics.length; j++) {
                    var t = group.topics[j];
                    if (t.text.toLowerCase().indexOf(q) !== -1
                        || t.key.toLowerCase().indexOf(q) !== -1) {
                        topics.push(t);
                    }
                }
                if (topics.length) {
                    result.push({ key: group.key, label: group.label, topics: topics });
                }
            }
            return result;
        }
    },

    methods: {
        onTopicClick(event, key) {
            // Let the browser handle anything but a plain left click (modifier keys, middle-click,
            // right-click). Only hijack the navigation for the simple "click the link" case.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                return;
            }
            event.preventDefault();

            // Collapse the mobile drawer after selection.
            this.mobileOpen = false;

            // Update the URL query param without a full page load.
            const url = new URL(window.location.href);
            url.searchParams.set('topic', key);
            window.history.pushState({ topic: key }, '', url.toString());

            this.$emit('navigate', key);
        }
    }
};
</script>

<style scoped>
/* Desktop: aside sticks to the top of the viewport as the content column scrolls past.
   Sticky is applied to the outer <aside> (not the inner <nav>) so its containing block is
   the tall `.guide-layout` flex row, giving sticky real room to work. `align-self: flex-start`
   prevents flex from stretching the aside to content height, keeping its natural height.
   The aside itself gets max-height + overflow-y so when the topic list is taller than the
   viewport, the aside scrolls internally without affecting page scroll. Mirrors the pattern
   used by GuideToc.vue on the right rail. */
.guide-sidebar {
    width: 260px;
    flex-shrink: 0;
    position: sticky;
    /* 50px sticky header in _LayoutPublic + 24px breathing room */
    top: 74px;
    align-self: flex-start;
    /* Viewport minus header (50px) and top/bottom gaps (24px each) */
    max-height: calc(100vh - 98px);
    overflow-y: auto;
}

.guide-sidebar__mobile-toggle {
    display: none;
}

.guide-sidebar__nav {
    padding: 0 8px;
}

.guide-sidebar__group {
    margin-bottom: 22px;
}

.guide-sidebar__group-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
    padding: 0 12px;
    margin-bottom: 6px;
}

.guide-sidebar__topics {
    list-style: none;
    padding: 0;
    margin: 0;
}

.guide-sidebar__topic {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 12px;
    border-radius: 6px;
    color: #334155;
    text-decoration: none;
    font-size: 13.5px;
    line-height: 1.3;
    transition: background 100ms ease-out, color 100ms ease-out;
}

.guide-sidebar__topic:hover {
    background: #f1f5f9;
    color: #0f172a;
}

.guide-sidebar__topic--active,
.guide-sidebar__topic--active:hover {
    background: #EEF2FF;
    color: #3730A3;
    font-weight: 600;
}

.guide-sidebar__topic-icon {
    color: #64748b;
    flex-shrink: 0;
}

.guide-sidebar__topic--active .guide-sidebar__topic-icon {
    color: #4F46E5;
}

.guide-sidebar__topic-label {
    flex: 1;
    min-width: 0;
}

.guide-sidebar__search {
    padding: 0 4px 14px;
}

.guide-sidebar__search-box {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;
    transition: border-color 150ms ease-out;
}

.guide-sidebar__search-box:focus-within {
    border-color: #94a3b8;
}

.guide-sidebar__search-icon {
    color: #94a3b8;
    flex-shrink: 0;
}

.guide-sidebar__search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #334155;
    font-family: inherit;
}

.guide-sidebar__search-input::placeholder {
    color: #94a3b8;
}

.guide-sidebar__search-clear {
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
    border-radius: 50%;
    padding: 2px;
    transition: color 100ms ease-out;
}

.guide-sidebar__search-clear:hover {
    color: #475569;
}

.guide-sidebar__no-results {
    padding: 12px 12px;
    font-size: 13px;
    color: #94a3b8;
    text-align: center;
}

/* Mobile: collapse the nav behind a toggle button at the top of the page and reset the
   desktop sticky behaviour - on a narrow viewport the aside sits above the content, not
   beside it, so there's nothing to stick to. */
@media (max-width: 900px) {
    .guide-sidebar {
        width: 100%;
        margin-bottom: 16px;
        position: static;
        max-height: none;
        overflow-y: visible;
    }

    .guide-sidebar__mobile-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 14px;
        border-radius: 6px;
        border: 1px solid #e2e8f0;
        background: #f8fafc;
        color: #0f172a;
        font-weight: 600;
        cursor: pointer;
        user-select: none;
    }

    .guide-sidebar__mobile-toggle:hover {
        background: #f1f5f9;
    }

    .guide-sidebar__nav {
        display: none;
        margin-top: 8px;
        padding: 8px;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        background: #fff;
    }

    .guide-sidebar__nav--open {
        display: block;
    }
}
</style>
